import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ConfirmModalConfig {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  icon?: string;
  itemName?: string; // For displaying "Delete {itemName}?" pattern
}

@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './confirm-modal.component.html',
  styleUrls: ['./confirm-modal.component.scss']
})
export class ConfirmModalComponent {
  @Input() config: ConfirmModalConfig = {
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmButtonClass: 'btn-primary',
    icon: 'bi-question-circle'
  };

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  isOpen = signal(false);
  isProcessing = signal(false);

  /**
   * Open the confirmation modal
   */
  open(config?: Partial<ConfirmModalConfig>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.isOpen.set(true);
  }

  /**
   * Close the modal without confirming
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Confirm the action
   */
  confirm(): void {
    this.confirmed.emit();
    this.isOpen.set(false);
  }

  /**
   * Set processing state (for async operations)
   */
  setProcessing(processing: boolean): void {
    this.isProcessing.set(processing);
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }

  // Preset configurations for common actions
  static deleteConfig(itemName: string): ConfirmModalConfig {
    return {
      title: 'Delete ' + itemName,
      message: `Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash',
      itemName
    };
  }

  static archiveConfig(itemName: string): ConfirmModalConfig {
    return {
      title: 'Archive ' + itemName,
      message: `Are you sure you want to archive this ${itemName.toLowerCase()}? You can restore it later from the archive.`,
      confirmText: 'Archive',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-warning',
      icon: 'bi-archive',
      itemName
    };
  }
}
